import React, { useState, useEffect } from 'react';
import RaisedButton from 'material-ui/RaisedButton';
import Checkbox from 'material-ui/Checkbox';
import Formsy from 'formsy-react';
import { FormsyText } from 'formsy-material-ui/lib';
import ApiSelector from '../../../containers/common/selectors/selectorContainer';
import {
  getActivitiesEndpointForTreatmentInstance,
  getActivitiesEndpointForVisitType,
  notDoneReasonsEndpoint,
} from '../../../settings/constants';
import globals from '../../../globals/communityGlobals';
import { positiveNumbersWithZero } from '../../../settings/constants/regex';
import { textFieldStyle, dropDownStyle } from '../../../theme/AllocateBaseTheme';
import { convertHoursToMinutes, convertMinutesToHours } from '../../../utils/datetimeconverter';

const locale = globals.getArea('visitActuals');
const errorsLocale = globals.getArea('error');

let isNotDoneReasonFieldInvalid = false;

export default function VisitActualActivityForm({
  activity,
  treatmentInstanceId,
  onRequestClose,
  onRequestSubmit,
  disabledActivities = [],
  visitType,
  isSuccessfullyCompleted,
  filterActivitiesByOrgUnit,
}) {
  const [state, setState] = useState({
    canSubmit: false,
    isCompleted: true,
    selected: [],
    selectedMinutesSpent: undefined,
    selectedHoursSpent: undefined,
    reason: '',
    selectedActivity: null,
    orgUnitsIds: [],
  });

  useEffect(() => {
    if (activity && (!state.reason || state.reason.length === 0)) {
      const activityHours = activity.minutesSpent ? convertMinutesToHours(activity.minutesSpent) : undefined;
      const activityMins = activity.minutesSpent
        ? activity.minutesSpent - convertHoursToMinutes(activityHours)
        : undefined;
      setState({
        ...state,
        isCompleted: activity.isCompleted === null ? true : activity.isCompleted,
        isAdditional: !!activity.isAdditional,
        selectedMinutesSpent: activityMins,
        selectedHoursSpent: activityHours,
        reason: activity.reason,
        selectedActivity: activity,
        orgUnitsIds: activity.orgUnitsIds,
      });
    }
  }, [isNotDoneReasonFieldInvalid]);

  const isEdit = !!activity && !!activity.id;

  const enableButton = () => {
    if (state.isCompleted && !state.selectedHoursSpent && !state.selectedMinutesSpent) {
      setState({ ...state, canSubmit: false });
    } else setState({ ...state, canSubmit: true });
  };

  const disableButton = () => {
    setState({ ...state, canSubmit: false });
  };

  const keepName = (event, key, id, selectedActivity) => {
    setState({ ...state, selectedActivity });
  };

  const saveActivity = () => {
    if (!state.canSubmit) {
      return;
    }
    disableButton();
    const minutesSpent =
      state.selectedMinutesSpent && state.selectedMinutesSpent !== '' ? parseInt(state.selectedMinutesSpent, 10) : 0;
    const minutes = state.selectedHoursSpent
      ? minutesSpent + convertHoursToMinutes(state.selectedHoursSpent)
      : minutesSpent;
    const activitySubmitted = Object.assign({
      isCompleted: state.isCompleted,
      isAdditional: state.isAdditional,
      id: state.selectedActivity.id,
      minutesSpent: state.isCompleted ? minutes : null,
      duration: state.selectedActivity ? state.selectedActivity.duration : 0,
      name: state.selectedActivity ? state.selectedActivity.name : activity.name,
      hoursSpent: state.selectedHoursSpent,
      reason: !state.isCompleted ? state.reason : null,
      orgUnitsIds: state.selectedActivity.orgUnitsIds,
    });

    onRequestSubmit(activitySubmitted);
    onRequestClose();
  };

  const filterOptions = options => {
    let filtered = options.filter(a => disabledActivities.indexOf(a.id) < 0);
    if (visitType) {
      filtered = filterActivitiesByOrgUnit(options);
    }
    return filtered;
  };

  const handleCompleted = () => {
    setState({ ...state, isCompleted: !state.isCompleted });
  };

  return (
    <div className="daily-planning-view">
      <Formsy.Form onValid={enableButton} onInvalid={disableButton}>
        <div className="row">
          <div className="col-c12">
            <ApiSelector
              name="id"
              endpoint={
                treatmentInstanceId
                  ? getActivitiesEndpointForTreatmentInstance(treatmentInstanceId)
                  : getActivitiesEndpointForVisitType(visitType)
              }
              reduxIdentifier="visitActualActivityId"
              required
              disabled={isEdit || isSuccessfullyCompleted}
              value={activity || undefined}
              onChange={keepName}
              floatingLabelText={locale.getMsg('activity')}
              isFetchRequired
              filterOptions={filterOptions}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-c6">
            {state.isCompleted && (
              <FormsyText
                name="hoursSpent"
                type="number"
                disabled={isSuccessfullyCompleted}
                validations={{
                  max: 23,
                  matchRegexp: positiveNumbersWithZero,
                  nonZeroValue: (values, value) => {
                    return (values.minutesSpent !== '' && values.minutesSpent !== '0') || value !== '0';
                  },
                }}
                validationErrors={{
                  max: errorsLocale.getMsg('validationNumberRange', ['0-23']),
                  matchRegexp: errorsLocale.getMsg('validationPositiveNumericError'),
                  nonZeroValue: errorsLocale.getMsg('cannotBeZeroWhenOtherFieldIs', [locale.getMsg('minutes')]),
                }}
                value={state.selectedHoursSpent}
                className="formsytext-css"
                floatingLabelStyle={textFieldStyle.floatingLabelStyle}
                floatingLabelShrinkStyle={textFieldStyle.floatingLabelShrinkStyle}
                style={textFieldStyle.style}
                inputStyle={textFieldStyle.inputStyle}
                underlineStyle={textFieldStyle.underlineStyle}
                underlineFocusStyle={textFieldStyle.underlineFocusStyle}
                validationColor={textFieldStyle.validationColor}
                floatingLabelText={`${locale.getMsg('hours')} ${state.isCompleted ? '*' : ''}`}
                onChange={event => setState({ ...state, selectedHoursSpent: event.target.value })}
              />
            )}
          </div>
          <div className="col-c6">
            {state.isCompleted && (
              <FormsyText
                name="minutesSpent"
                type="number"
                disabled={isSuccessfullyCompleted}
                validations={{
                  max: 59,
                  matchRegexp: positiveNumbersWithZero,
                  nonZeroValue: (values, value) => {
                    return (values.hoursSpent !== '' && values.hoursSpent !== '0') || value !== '0';
                  },
                }}
                validationErrors={{
                  max: errorsLocale.getMsg('validationNumberRange', ['0-59']),
                  matchRegexp: errorsLocale.getMsg('validationPositiveNumericError'),
                  nonZeroValue: errorsLocale.getMsg('cannotBeZeroWhenOtherFieldIs', [locale.getMsg('hours')]),
                }}
                value={state.selectedMinutesSpent}
                className="formsytext-css"
                floatingLabelStyle={textFieldStyle.floatingLabelStyle}
                floatingLabelShrinkStyle={textFieldStyle.floatingLabelShrinkStyle}
                style={textFieldStyle.style}
                inputStyle={textFieldStyle.inputStyle}
                underlineStyle={textFieldStyle.underlineStyle}
                underlineFocusStyle={textFieldStyle.underlineFocusStyle}
                validationColor={textFieldStyle.validationColor}
                floatingLabelText={`${locale.getMsg('minutes')} ${state.isCompleted ? '*' : ''}`}
                onChange={event => setState({ ...state, selectedMinutesSpent: event.target.value })}
              />
            )}
          </div>
        </div>

        <div className="row">
          <div className="col-c6">
            <Checkbox
              style={{ paddingTop: '20px' }}
              checked={state.isCompleted}
              onCheck={() => handleCompleted()}
              label={locale.getMsg('completed')}
              disabled={isSuccessfullyCompleted}
            />
          </div>
        </div>

        {!state.isCompleted && (
          <div className="row">
            <ApiSelector
              name="notDoneReasonsContainer"
              endpoint={notDoneReasonsEndpoint}
              reduxIdentifier="notDoneReasons"
              required
              disabled={isSuccessfullyCompleted}
              value={state.reason}
              className="select-field"
              floatingLabelText={`${locale.getMsg('reason')} *`}
              isFetchRequired
              mapWithoutId
              setValueValidity={data => {
                isNotDoneReasonFieldInvalid = data;
              }}
              onChange={event => setState({ ...state, reason: event.target.innerText })}
              errorText={
                isNotDoneReasonFieldInvalid &&
                state.reason &&
                state.reason.length > 0 &&
                locale.getMsg('invalidNotDoneReason', [state.reason])
              }
              errorStyle={dropDownStyle.errorStyle}
              shouldCompareNatural={false}
            />
          </div>
        )}

        <div className="row">
          <div className="col-c6" />
          <div className="col-c3">
            <RaisedButton className="align-right" label={locale.getMsg('cancel')} onClick={() => onRequestClose()} />
          </div>
          {!state.isCompleted && <div className="input-gap" />}
          <div className="col-c3">
            <RaisedButton
              primary
              className="align-right"
              label={activity ? locale.getMsg('editSubmitLabel') : locale.getMsg('add')}
              disabled={!state.canSubmit || isSuccessfullyCompleted}
              onClick={saveActivity}
            />
          </div>
        </div>
      </Formsy.Form>
    </div>
  );
}
